/**
 * AddPaymentServiceCtrl controller
 * Add/Edit Payment services
 */

import { PAYMENT_SYSTEM_TYPES } from 'app/core/constants'

/** Currencies with zero minor units (pattern / step for min amount rows). */
const ZERO_DECIMAL_CURRENCY_NAMES = ['JPY', 'KRW', 'VND', 'CLP', 'PYG', 'UGX', 'XAF', 'XOF', 'ISK']

/** Stable RegExp refs for ng-pattern — literals inside a function create new instances each $digest (infdig). */
const MIN_AMOUNT_PATTERN_TWO_DECIMALS = /^[0-9]*\.?[0-9]*$/
const MIN_AMOUNT_PATTERN_ZERO_DECIMALS = /^\d+$/
const MIN_AMOUNT_PATTERN_GC_TWO_DECIMALS = /^\d*(\.\d{0,2})?$/

// controller mixins
AddPaymentServiceCtrl.mixes = [
  ['m2mCtrlMixin', 'payment', 'currencies']
]

/**
 * AddPaymentServiceCtrl controller
 *
 * @class AddPaymentServiceCtrl
 * @memberof mwApp/settings
 * @see mwApp/settings
 * @mixes mwApp/common.mixin:m2mCtrlMixin
 *
 * @ngdoc controller
 * @name mwApp/settings.controller:AddPaymentServiceCtrl
 */
/* @ngInject */
export default function AddPaymentServiceCtrl ($uibModalInstance, PaymentSystem, NetworkPaymentSystem, payment, authSvc) {
  var vm = this

  /**
   * Has access
   *
   * @memberOf mwApp/settings.controller:AddPaymentServiceCtrl
   * @function
   */
  vm.hasAccess = function (access) {
    return authSvc.hasAccess(access)
  }

  /**
   * Payment system types
   *
   * @memberof mwApp/packages.controller:AddPaymentServiceCtrl
   * @name vm.payment_system_types
   * @type {Object}
   */
  vm.payment_system_types = PAYMENT_SYSTEM_TYPES

  /**
   * PaymentSystem
   *
   * @memberof mwApp/settings.controller:AddPaymentServiceCtrl
   * @type {models.PaymentSystem}
   */
  vm.payment = payment

  if (payment.paymentSystemType === 'GOCARDLESS') {
    const existing = payment.webhookSettings && typeof payment.webhookSettings === 'object' ? payment.webhookSettings : {}
    payment.webhookSettings = {
      enabled: false,
      ...existing,
      secret: existing.secret ?? ''
    }
    vm.initialGoCardlessWebhook = angular.copy(payment.webhookSettings || {})
  }

  if (payment.paymentSystemType === 'PAYPAL') {
    const existing = payment.webhookSettings && typeof payment.webhookSettings === 'object' ? payment.webhookSettings : {}
    payment.webhookSettings = {
      enabled: false,
      ...existing,
      webhookId: existing.webhookId ?? ''
    }
    if (typeof payment.enableTransactionFee !== 'boolean') payment.enableTransactionFee = false
    if (payment.defaultTransactionFeePercent == null || payment.defaultTransactionFeePercent === '') payment.defaultTransactionFeePercent = 0
    if (payment.live == null) payment.live = false
    if (payment.autoPay == null) payment.autoPay = false
  }

  /**
   * isNew flag
   *
   * @memberof mwApp/settings.controller:AddPaymentServiceCtrl
   * @type {Boolean}
   */
  vm.isNew = payment._isNew();

  /**
   * Currencies list
   *
   * @memberof mwApp/settings.controller:AddPaymentServiceCtrl
   * @name vm.currencies
   * @type {models.Currency[]}
   */

  // get currencies from extra
  ((payment instanceof NetworkPaymentSystem)
    ? NetworkPaymentSystem.loadExtra(NetworkPaymentSystem.extraFor(payment))
    : PaymentSystem.loadExtra(PaymentSystem.extraFor(payment))
  )
    .to(vm)
    .then(data => vm.payment.paymentSystemType === 'NEWEBPAY' ? data.currencies.filter(cur => cur.name === 'TWD') : data.currencies || [])
    .except(payment.currencies) // remove currencies, already existed in payment
    .to(vm.currencies = [])
    .then(() => vm.countries)
    .except(payment.countries)
    .to(vm.countries = [])

  vm.countriesSelected = payment.countries.length || null

  vm.selectCountry = function (country) {
    vm.countries = vm.countries.filter(({ id }) => id !== country.id)
    vm.payment.countries.push(country)
  }

  vm.deselectCountry = function (country, form) {
    form.$setDirty()

    vm.countries.push(country)
    vm.payment.countries = vm.payment.countries.filter(({ id }) => id !== country.id)
    vm.countriesSelected = payment.countries.length > 0 || null
  }

  /**
   * Hack for validation
   * @memberof mwApp/packages.controller:AddPaymentServiceCtrl
   * @type {Array}
   */
  vm.currencySelected = payment.currencies.length || null

  vm.deselectCurrencyMain = (currency, form) => {
    vm.currencySelected = payment.currencies.length > 1 || null
    vm.deselectCurrency(currency, form)
  }

  vm.showLoginTooltip = function () {
    if (vm.payment.paymentSystemType === 'AIRWALLEX') {
      return 'You can find Account Client ID in: Airwallex Settings → Developer → API keys'
    }
    return (['CCPP', 'TRUE_MONEY', 'TRUE_ONLINE', 'TRUE_MOVE_H'].includes(vm.payment.paymentSystemType) && `Enter Merchant ID from your${['TRUE_ONLINE', 'TRUE_MOVE_H'].includes(vm.payment.paymentSystemType) ? '' : (' ' + vm.payment_system_types[vm.payment.paymentSystemType])} account`) ||
      'Login that is provided by Setplex'
  }

  /**
   * Create or edit payment, and close modal window afterwards
   *
   * @memberof mwApp/settings.controller:AddPaymentServiceCtrl
   * @param {Object} [form] Angular form controller — required for providers that strip
   *   secrets from the PUT payload when the field is pristine and empty (GoCardless, PayPal).
   */
  vm.submit = function (form) {
    vm.setDisable()
    var mapFunc
    if (payment.paymentSystemType === 'GOCARDLESS') {
      mapFunc = function (obj) {
        var ws = obj.webhookSettings
        if (!ws) return
        var secretTrimmed = ws.secret != null ? String(ws.secret).trim() : ''
        var wskPristine = form && form.gocardlessWebhookSecret && form.gocardlessWebhookSecret.$pristine
        if (
          secretTrimmed === '' &&
          wskPristine &&
          vm.initialGoCardlessWebhook &&
          vm.initialGoCardlessWebhook.enabled === true
        ) {
          delete ws.secret
          ws.enabled = true
        }
      }
    } else if (payment.paymentSystemType === 'PAYPAL') {
      mapFunc = function (obj) {
        var pwdTrimmed = obj.password != null ? String(obj.password).trim() : ''
        var pwdPristine = form && form.paypalClientSecret && form.paypalClientSecret.$pristine
        if (pwdTrimmed === '' && pwdPristine) {
          delete obj.password
        }
      }
    }

    payment._save(mapFunc)
      .then(
        () => {
          payment.password = null
          $uibModalInstance.close(payment)
        }
      ).finally(vm.unsetDisable)
  }

  vm.addCurrency = function (form) {
    form.$setDirty()
    vm.payment.currencies.push(vm.currencies.filter(vm.filterCurrencies)[0])
    vm.currencies = vm.currencies.filter(vm.filterCurrencies)
  }

  vm.deleteCurrency = function (index, currency, form) {
    vm.payment.currencies.splice(index, 1)
    vm.currencies.push(currency)
    form.$setDirty()
  }

  vm.selectGr4vyCurrency = function (currency, index) {
    vm.payment.currencies[index] = currency
  }

  vm.filterCurrencies = function (curr) {
    return !vm.payment.currencies.find(c => c.id === curr.id)
  }

  vm.filteredPaymentMethods = function () {
    return vm.payment_methods.filter(pm => !vm.payment.paymentMethods.find(cpm => cpm.paymentMethod === pm))
  }

  vm.mapLoginLabel = function () {
    if (['CCPP', 'TRUE_MONEY', 'TRUE_ONLINE', 'TRUE_MOVE_H'].includes(vm.payment.paymentSystemType)) return 'Merchant ID'
    if (vm.payment.paymentSystemType === 'AIRWALLEX') return 'Client ID'
    return 'Login'
  }

  vm.addPaymentMethod = function () {
    vm.payment.paymentMethods.push({ paymentMethod: null, transactionFeePercent: 0 })
  }

  vm.payment_methods = ['GOOGLEPAY', 'APPLEPAY', 'CREDIT_CARD']
  vm.payment_methods_labels = { GOOGLEPAY: 'Google Pay', APPLEPAY: 'Apple Pay', CREDIT_CARD: 'Cards' }
  vm.webhookSettingsEnabled = window.ENV.APP_AIRWALLEX_WEBHOOK_SETTINGS_ENABLED

  vm.minAmountDecimals = function (currency) {
    if (!currency || !currency.name) return 2
    return ZERO_DECIMAL_CURRENCY_NAMES.includes(currency.name) ? 0 : 2
  }

  vm.currencySelectPlaceholder = function () {
    return vm.payment.paymentSystemType === 'GOCARDLESS' ? 'None' : 'Choose currency'
  }

  vm.currencySettingsRowTooltip = function () {
    return vm.payment.paymentSystemType === 'GOCARDLESS'
      ? 'Enter the minimum amount in a currency supported by the GoCardless'
      : 'Select required currencies which are equal to currencies of configured payment systems.'
  }

  vm.minAmountFloorForRow = function (currency) {
    if (vm.payment.paymentSystemType !== 'GOCARDLESS') return 0.1
    return currency && currency.name === 'USD' ? 2 : 0.1
  }

  vm.minAmountStepForRow = function (currency) {
    if (vm.payment.paymentSystemType !== 'GOCARDLESS') return 0.01
    return vm.minAmountDecimals(currency) === 0 ? 1 : 0.01
  }

  vm.minAmountPatternForRow = function (currency) {
    if (vm.payment.paymentSystemType !== 'GOCARDLESS') {
      return MIN_AMOUNT_PATTERN_TWO_DECIMALS
    }
    if (!currency || !currency.name) {
      return MIN_AMOUNT_PATTERN_TWO_DECIMALS
    }
    const n = vm.minAmountDecimals(currency)
    if (n === 0) return MIN_AMOUNT_PATTERN_ZERO_DECIMALS
    return MIN_AMOUNT_PATTERN_GC_TWO_DECIMALS
  }

  vm.minAmountMinErrorMessage = function (currency) {
    return currency && currency.name === 'USD' ? 'Amount must be at least $2 USD' : 'Minimal amount should be higher than 0.1'
  }

  vm.minAmountPatternErrorMessage = function (currency) {
    const n = vm.minAmountDecimals(currency)
    return `Can only be ${n} digits after the decimal point`
  }

  vm.minAmountStepErrorMessage = vm.minAmountPatternErrorMessage

  vm.currencyMinPatternMessage = function (currency) {
    return vm.payment.paymentSystemType === 'GOCARDLESS'
      ? vm.minAmountPatternErrorMessage(currency)
      : 'Digits only'
  }

  vm.currencyMinFloorMessage = function (currency) {
    return vm.payment.paymentSystemType === 'GOCARDLESS'
      ? vm.minAmountMinErrorMessage(currency)
      : 'Minimal amount should be higher than 0.1'
  }

  vm.currencyMinStepMessage = function (currency) {
    return vm.payment.paymentSystemType === 'GOCARDLESS'
      ? vm.minAmountStepErrorMessage(currency)
      : 'Enter a valid number with up to 2 decimal places.'
  }

  /**
   * Cancel editing, close modal window
   *
   * @memberof mwApp/settings.controller:AddPaymentServiceCtrl
   */
  vm.cancel = function () {
    $uibModalInstance.dismiss('cancel')
  }
}
